<div class="gl-page">
  <!-- Back link -->
  <yds-button-back text="Ga terug 'Overzicht boekingsgroep'" (back)="goBack()" />

  <!-- Title + page actions -->
  <div class="gl-page__header">
    <h1 class="gl-page__title">Boekingsgroep</h1>
    <div class="gl-page__header-actions">
      @if (!isAddMode()) {
        <yds-button (click)="goToBookingDefinition()">Boekingsdefinitie</yds-button>
        <yds-button (click)="goToBookingRule()">Boekingsregel</yds-button>
        <yds-button variant="destructive" (click)="onDelete()">Verwijderen</yds-button>
      }
    </div>
  </div>

  @if (isLoading()) {
    <div class="gl-detail-loading">
      <yds-spinner [label]="'Boekingsgroep laden...'" />
    </div>
  } @else if (notFound()) {
    <p class="bge-empty">Er zijn geen details voor boekingsgroep gevonden.</p>
  } @else {
    <!-- Details card -->
    <yds-card-editable
      #detailsCard
      title="Details"
      [(editing)]="detailsEditing"
      [nextDisabled]="isSaving() || form.invalid"
      [nextLabel]="isAddMode() ? 'Volgende' : 'Opslaan'"
      (cancel)="onDetailsCancel()"
      (next)="onDetailsNext()"
    >
      @if (detailsEditing()) {
        <form class="gl-form" [formGroup]="form">
          <p class="gl-form__required-hint">
            Velden gemarkeerd met een sterretje
            (<span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span>) zijn verplicht.
          </p>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Boekingsgroep 
            </label>
            <div class="gl-field__control">
              <yds-text-field
                formControlName="bookingGroupName"
                [required]="true"
                [hasError]="isInvalid(form.get('bookingGroupName'))"
                [errorMsg]="'Boekingsgroep is verplicht.'"
                [placeholder]="'Voer een boekingsgroep in'"
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Grootboek 
            </label>
            <div class="gl-field__control">
              @if (isAddMode()) {
                <yds-combobox
                  formControlName="generalLedgerId"
                  [items]="generalLedgerOptions()"
                  [required]="true"
                  [hasError]="isInvalid(form.get('generalLedgerId'))"
                  [errorMsg]="'Grootboek is verplicht.'"
                  [placeholder]="'Selecteer een grootboek'"
                />
              } @else {
                <p class="gl-field__readonly-text">{{ grootboekName() }}</p>
              }
            </div>
          </div>

          <div class="gl-field gl-field--inline">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Boeken per uitvoercode 
            </label>
            <div class="gl-field__control">
              <yds-switch formControlName="bookByWageType" />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">
             <span class="gl-form__required-hint-asterisk" aria-hidden="true">*</span> Begindatum 
            </label>
            <div class="gl-field__control">
              <yds-date-picker
                formControlName="startDate"
                [required]="true"
                [locale]="translate.getCurrentLang()"
                [placeholder]="'dd-mm-jjjj'"
                [allowTodayAction]="false"
                [dateFilter]="firstDayOfMonthFilter"
                [dateFilterErrorKey]="null"
                [dateFilterError]="'Alleen 1ste van de maand is toegestaan'"
                [errorMessage]="
                  isInvalid(form.get('startDate'))
                    ? (form.get('startDate')?.hasError('firstOfMonth')
                        ? 'Alleen 1ste van de maand is toegestaan'
                        : 'Begindatum is verplicht.')
                    : ''
                "
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">Einddatum</label>
            <div class="gl-field__control">
              <yds-date-picker
                formControlName="endDate"
                [locale]="translate.getCurrentLang()"
                [placeholderKey]="null"
                [placeholder]="''"
                [allowTodayAction]="false"
                [dateFilter]="lastDayOfMonthFilter"
                [dateFilterErrorKey]="null"
                [dateFilterError]="'Alleen laatste dag van de maand toegestaan of leeg laten'"
                [errorMessage]="
                  isInvalid(form.get('endDate')) && form.get('endDate')?.hasError('lastOfMonth')
                    ? 'Alleen laatste dag van de maand toegestaan of leeg laten'
                    : form.hasError('startBeforeEnd') && form.get('endDate')?.touched
                      ? 'Begindatum moet voor einddatum liggen'
                      : ''
                "
              />
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-field__label">Memo</label>
            <div class="gl-field__control">
              <yds-text-area
                formControlName="memoValue"
                [placeholder]="'Voer een memo in'"
                [maxLength]="500"
                [showCounter]="true"
              />
            </div>
          </div>
        </form>
      } @else {
        <div class="gl-detail">
          <div class="gl-detail__row">
            <span class="gl-detail__label">Boekingsgroep</span>
            <span class="gl-detail__value">{{ bookingGroupName() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Grootboek</span>
            <span class="gl-detail__value">{{ grootboekName() }}</span>
          </div>
          <div class="gl-detail__row gl-detail__row--inline">
            <span class="gl-detail__label">Boeken per uitvoercode</span>
            <span class="gl-detail__value gl-detail__value--setting">
              @if (bookByWageTypeOn()) {
                <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-checkmark" aria-hidden="true"></span>
                <span>Aan</span>
              } @else {
                <span>Uit</span>
              }
            </span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Begindatum</span>
            <span class="gl-detail__value">{{ startDateDisplay() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Einddatum</span>
            <span class="gl-detail__value">{{ endDateDisplay() }}</span>
          </div>
          <div class="gl-detail__row">
            <span class="gl-detail__label">Memo</span>
            <span class="gl-detail__value gl-detail__value--memo">{{ memoDisplay() }}</span>
          </div>
        </div>
      }
    </yds-card-editable>

    @if (!isAddMode()) {
      <!-- Criteria card -->
      <yds-card-editable
        class="bgc-criteria-card"
        title="Criteria"
        [(editing)]="criteriaEditing"
        nextLabel="Opslaan"
        [nextDisabled]="isSavingCriteria()"
        (cancel)="onCriteriaCancel()"
        (next)="onCriteriaSave()"
      >
        @if (criteriaEditing()) {
          <div class="gl-grid-wrapper">
            <table class="gl-grid">
              <thead>
                <tr>
                  <th class="gl-grid__col--criterium">Criterium</th>
                  <th class="gl-grid__col--num">Vanaf</th>
                  <th class="gl-grid__col--num">Lengte</th>
                  <th class="gl-grid__col--conversion">Conversielabel</th>
                  <th class="gl-grid__col--action"></th>
                </tr>
              </thead>
              <tbody #criteriaTableBody>
                @if (criteriaRows().length === 0) {
                  <tr class="gl-grid__empty">
                    <td colspan="5">Nog geen criteria toegevoegd.</td>
                  </tr>
                } @else {
                  @for (row of criteriaRows(); track $index) {
                    <tr>
                      <td class="gl-grid__col--criterium">
                        <yds-dropdown
                          [items]="criterionElementOptionsForRow()"
                          [ngModel]="row.elementId"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onCriterionElementChange($index, $event)"
                          [required]="true"
                          [allowClear]="false"
                          [placeholder]="'Selecteer een criterium'"
                          [hasError]="isCriterionRowInvalid(row)"
                          [errorMsg]="isCriterionRowInvalid(row) ? 'Selecteer een criterium' : null"
                        />
                      </td>
                      <td class="gl-grid__col--num">
                        <input
                          type="number"
                          class="gl-grid__num"
                          [ngModel]="row.startPosition"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onCriterionNumberChange($index, 'startPosition', $event)"
                        />
                      </td>
                      <td class="gl-grid__col--num">
                        <input
                          type="number"
                          class="gl-grid__num"
                          [ngModel]="row.numberOfPositions"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onCriterionNumberChange($index, 'numberOfPositions', $event)"
                        />
                      </td>
                      <td class="gl-grid__col--conversion">
                        <yds-combobox
                          [items]="conversionTableOptions()"
                          [ngModel]="row.conversionTableId"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onCriterionConversionTableChange($index, $event)"
                          [placeholder]="'Selecteer een conversielabel'"
                        />
                      </td>
                      <td class="gl-grid__col--action">
                        <button
                          type="button"
                          class="gl-grid__remove"
                          aria-label="Verwijder criterium"
                          (click)="removeCriterionRow($index)"
                        >
                          <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-delete" aria-hidden="true"></span>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>

          @if (canAddCriterion()) {
            <button type="button" class="gl-grid__add-link" (click)="addCriterionRow()">
              + Criterium toevoegen
            </button>
          }
        } @else {
          @if (criteriaRows().length === 0) {
            <p class="bge-empty">Nog geen criteria toegevoegd.</p>
          } @else {
            <div class="gl-grid-wrapper">
              <table class="gl-grid">
                <thead>
                  <tr>
                    <th class="gl-grid__col--criterium">Criterium</th>
                    <th class="gl-grid__col--num">Vanaf</th>
                    <th class="gl-grid__col--num">Lengte</th>
                    <th class="gl-grid__col--conversion">Conversielabel</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of criteriaRows(); track $index) {
                    <tr>
                      <td class="gl-grid__col--criterium">
                        <span class="gl-grid__truncate" [title]="criterionRowLabel(row)">{{ criterionRowLabel(row) }}</span>
                      </td>
                      <td class="gl-grid__col--num">{{ row.startPosition ?? 0 }}</td>
                      <td class="gl-grid__col--num">{{ row.numberOfPositions ?? 0 }}</td>
                      <td class="gl-grid__col--conversion">{{ conversionTableLabel(row) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </yds-card-editable>

      <!-- Niet signaleren card -->
      <yds-card-editable
        class="bgc-criteria-card"
        title="Niet signaleren"
        [(editing)]="excludedEditing"
        nextLabel="Opslaan"
        [nextDisabled]="isSavingExcluded()"
        (cancel)="onExcludedCancel()"
        (next)="onExcludedSave()"
      >
        @if (excludedEditing()) {
          <div class="gl-grid-wrapper">
            <table class="gl-grid gl-grid--excluded">
              <thead>
                <tr>
                  <th class="gl-grid__col--criterium">Uitvoercode</th>
                  <th class="gl-grid__col--action"></th>
                </tr>
              </thead>
              <tbody>
                @if (excludedRows().length === 0) {
                  <tr class="gl-grid__empty">
                    <td colspan="2">Nog geen uitvoercode toegevoegd.</td>
                  </tr>
                } @else {
                  @for (row of excludedRows(); track $index) {
                    <tr>
                      <td class="gl-grid__col--criterium">
                        <yds-combobox
                          [items]="excludableElementOptionsForRow($index)"
                          [ngModel]="row.elementId || null"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onExcludedElementChange($index, $event)"
                          [required]="true"
                          [allowClear]="false"
                          [placeholder]="'Selecteer een uitvoercode'"
                          [hasError]="isExcludedRowInvalid(row)"
                          [errorMsg]="isExcludedRowInvalid(row) ? 'Uitvoercode is verplicht.' : null"
                        />
                      </td>
                      <td class="gl-grid__col--action">
                        <button
                          type="button"
                          class="gl-grid__remove"
                          aria-label="Verwijder uitvoercode"
                          (click)="removeExcludedRow($index)"
                        >
                          <span class="vismaicon vismaicon-dynamic vismaicon-sm vismaicon-delete" aria-hidden="true"></span>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>

          <button type="button" class="gl-grid__add-link" (click)="addExcludedRow()">
            + Uitvoercode toevoegen
          </button>
        } @else {
          @if (excludedRows().length === 0) {
            <p class="bge-empty">Nog geen uitvoercode toegevoegd.</p>
          } @else {
            <div class="gl-grid-wrapper">
              <table class="gl-grid gl-grid--excluded">
                <thead>
                  <tr>
                    <th class="gl-grid__col--criterium">Uitvoercode</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of excludedRows(); track $index) {
                    <tr>
                      <td class="gl-grid__col--criterium">{{ excludedRowLabel(row) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </yds-card-editable>
    }
  }
</div>
